import type { FormEvent, ReactElement } from 'react'
import { useState } from 'react'
import { chartCss } from '~/charts/theme'
import type {
  CredentialBinding,
  CredentialRecord,
  CredentialStanding,
  IssuedInvitation,
  Membership,
  Session,
  TenantRole,
} from '~/state/auth'
import {
  activeMembership,
  bindingOf,
  dayOf,
  hasAuthenticator,
  holdingsOf,
  invitationLink,
  standingOf,
  transportsOf,
  useAccountDeletion,
  useAddCredential,
  useCreateTenant,
  useDeleteAccount,
  useInvitations,
  useInvite,
  useRemoveCredential,
  useSession,
} from '~/state/auth'
import { daysBetween, describeAge, describeDuration, today } from '~/state/dates'

import { Card, CardHead } from '~/components/ui/Card'
import { Button } from '~/components/ui/Controls'
import { StatusPill } from '~/components/ui/Status'
import { DataTable } from '~/components/ui/Table'

const BINDING_LABEL: Record<CredentialBinding, string> = {
  synced: 'Synced',
  'device-bound': 'This device only',
}

const BINDING_NOTE: Record<CredentialBinding, string> = {
  synced: 'Your platform keeps a copy, so it survives this device being lost.',
  'device-bound': 'It exists nowhere else. Lose the device and this key is gone.',
}

const PASSKEY_COLUMNS = [
  { id: 'passkey', label: 'Passkey' },
  { id: 'where', label: 'Where it lives', width: '280px' },
  { id: 'added', label: 'Added', width: '150px' },
  { id: 'used', label: 'Last used', width: '150px' },
  { id: 'actions', label: ' ', width: '250px' },
]

const HOUSEHOLD_COLUMNS = [
  { id: 'household', label: 'Household', width: '260px' },
  { id: 'role', label: 'Your role' },
  { id: 'open', label: 'Open', width: '130px' },
]

const PENDING_COLUMNS = [
  { id: 'to', label: 'Written to' },
  { id: 'role', label: 'Role', width: '150px' },
  { id: 'expires', label: 'Expires', width: '220px' },
]

const PAGE_CSS = `
/* Rows here run to several lines — a passkey's name and where it lives, a
   household's role and what the role can do — so a row is as tall as what is in
   it rather than one of the table's own. */
.nw-account .nw-data :where(td, tbody th) { height: auto; padding-bottom: 10px; padding-top: 10px; vertical-align: top; }
.nw-account .nw-lede { color: var(--nw-body-ink); font-size: var(--nw-text-cell); margin: 0 0 14px; max-width: 70ch; }
.nw-account td small { color: var(--nw-muted); display: block; font-size: var(--nw-text-chip); }
.nw-account .nw-row-actions { display: flex; flex-wrap: wrap; gap: var(--nw-space-2); justify-content: flex-end; }
.nw-account form.nw-add, .nw-account form.nw-invite { align-items: flex-end; display: flex; flex-wrap: wrap; gap: var(--nw-space-3); margin-top: var(--nw-space-4); }
.nw-account form :where(.nw-add, .nw-invite) label, .nw-account form.nw-add label, .nw-account form.nw-invite label { display: block; }
.nw-account form label span { color: var(--nw-faint); display: block; font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); letter-spacing: var(--nw-track-label); margin-bottom: var(--nw-space-2); text-transform: uppercase; }
.nw-account form.nw-add input { width: 240px; }
.nw-account form.nw-invite input { width: 280px; }
.nw-account .nw-role { color: var(--nw-muted); font-size: var(--nw-text-chip); letter-spacing: var(--nw-track-label); text-transform: uppercase; }
.nw-account .nw-confirm { color: var(--nw-critical); font-size: var(--nw-text-cell); }
.nw-account .nw-here { font-size: var(--nw-text-body); margin: 0 0 14px; }
.nw-account .nw-none { color: var(--nw-body-ink); font-size: var(--nw-text-cell); margin: 0; }
.nw-account h3 { color: var(--nw-faint); font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); letter-spacing: var(--nw-track-label); margin: var(--nw-space-6) 0 var(--nw-space-2); text-transform: uppercase; }
.nw-account .nw-role-note { color: var(--nw-muted); font-size: var(--nw-text-chip); margin: var(--nw-space-2) 0 0; max-width: 74ch; }
/* An email address is one unbroken word, and it appears four times in this block.
   Without somewhere to break it, an ordinary long address pushes the whole page
   sideways on a phone. "anywhere" rather than "break-word" because it also lets
   the block narrow inside a flex or grid parent, not only wrap within itself. */
.nw-account .nw-issued { border: 1px solid var(--nw-serious); border-radius: 10px; margin-top: 16px; overflow-wrap: anywhere; padding: 14px; }
.nw-account .nw-issued .nw-flag { margin-bottom: 12px; }
.nw-account .nw-issued label span { color: var(--nw-muted); display: block; font-size: var(--nw-text-chip); letter-spacing: var(--nw-track-label); margin-bottom: 4px; text-transform: uppercase; }
.nw-account .nw-issued input {
  background: var(--nw-plane); border: 1px solid var(--nw-hairline); border-radius: 8px;
  box-sizing: border-box; color: inherit; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: var(--nw-text-chip); padding: 7px 9px; width: 100%;
}
.nw-account .nw-issued .nw-row-actions { justify-content: flex-start; margin-top: 8px; }
/* What is about to be destroyed, one household per line, because a sentence
   that ran them together would be read as one thing rather than as several. */
.nw-account .nw-going { color: var(--nw-body-ink); font-size: var(--nw-text-cell); line-height: 1.55; margin: 0 0 14px; max-width: 74ch; padding-left: 18px; }
.nw-account .nw-going li { margin-bottom: 6px; }
.nw-account form.nw-add input#nw-retire-confirm { width: 320px; }
`

export function Account(): ReactElement {
  const { session, error } = useSession()

  return (
    <main className="nw nw-account">
      <style>{chartCss + PAGE_CSS}</style>
      <h1>Account</h1>
      <p className="nw-sub">{session ? session.user.email : 'Who you are, and how you get back in'}</p>

      {error && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>Could not read your account: {error.message}</span>
        </p>
      )}

      {!session && !error && <p className="nw-empty">Loading…</p>}
      {session && <Signed session={session} />}
    </main>
  )
}

function Signed({ session }: { session: Session }): ReactElement {
  const here = activeMembership(session)

  return (
    <>
      <Passkeys session={session} />
      <Households session={session} here={here} />
      {here?.role === 'owner' && <Invitations household={here} />}
      <NewHousehold first={session.tenants.length === 0} />
      <Retirement />
    </>
  )
}

function Passkeys({ session }: { session: Session }): ReactElement {
  const { credentials } = session

  return (
    <Card>
      <CardHead
        id="nw-passkeys-heading"
        title="Passkeys"
        lede="Every way into this dashboard, and nothing else. There is no password behind them and no recovery address to send a link to."
      />

      <Standing standing={standingOf(session)} />

      {credentials.length > 0 && (
        <DataTable columns={PASSKEY_COLUMNS} label="Every passkey on this sign-in">
          {credentials.map((credential) => (
            <Row
              key={credential.credentialId}
              credential={credential}
              onlyOne={credentials.length === 1}
            />
          ))}
        </DataTable>
      )}

      <AddPasskey />
    </Card>
  )
}

/**
 * The warning that matters most is the one nothing else on the page would give:
 * a set of passkeys that all die with their devices reads as "three ways in"
 * until the day a bag is stolen and it turns out to have been one.
 */
function Standing({ standing }: { standing: CredentialStanding }): ReactElement {
  if (standing === 'recoverable') {
    return (
      <p className="nw-note">
        At least one of these is synced by your platform, so losing a single device does not lock
        you out. It does not protect you from losing the platform account that syncs it.
      </p>
    )
  }

  if (standing === 'none') {
    return (
      <p className="nw-flag nw-flag--missing">
        <span className="nw-flag-icon" aria-hidden="true">
          ✕
        </span>
        <span>
          <b>No passkeys are registered</b> — the next sign-in has nothing to answer with. Register
          one now, on a device you will still have tomorrow.
        </span>
      </p>
    )
  }

  return (
    <p className="nw-flag nw-flag--stale">
      <span className="nw-flag-icon" aria-hidden="true">
        !
      </span>
      <span>
        <b>Every passkey you have is bound to its device</b> — none of them is synced anywhere else.
        If those devices are lost, broken or wiped, this dashboard is shut for good: there is no
        password to reset, no recovery email, and the history behind it — years of daily positions
        from banks, brokers and chains — cannot be collected again. Add a passkey your platform
        syncs, or one on a second device you keep elsewhere.
      </span>
    </p>
  )
}

function Row({
  credential,
  onlyOne,
}: {
  credential: CredentialRecord
  onlyOne: boolean
}): ReactElement {
  const [confirming, setConfirming] = useState(false)
  const remove = useRemoveCredential()
  const binding = bindingOf(credential)
  const transports = transportsOf(credential)

  return (
    <tr>
      <td>
        {credential.name ?? 'Unnamed passkey'}
        {transports.length > 0 && <small>{transports.join(', ')}</small>}
      </td>
      <td>
        <StatusPill
          tone={binding === 'synced' ? 'live' : 'caution'}
          label={BINDING_LABEL[binding]}
        />
        <small>{BINDING_NOTE[binding]}</small>
      </td>
      <td>
        <Since timestamp={credential.createdAt} never="unknown" />
      </td>
      <td>
        <Since timestamp={credential.lastUsedAt} never="never" />
      </td>
      <td>
        <div className="nw-row-actions">
          {onlyOne ? (
            <span className="nw-muted">Only way in — add another before removing this one.</span>
          ) : confirming ? (
            <>
              <span className="nw-confirm">Remove for good?</span>
              <Button
                kind="danger"
                disabled={remove.isPending}
                onClick={() => remove.mutate(credential.credentialId)}
              >
                {remove.isPending ? 'Touch your key…' : 'Remove'}
              </Button>
              <Button onClick={() => setConfirming(false)}>Keep</Button>
            </>
          ) : (
            <Button kind="danger" onClick={() => setConfirming(true)}>
              Remove
            </Button>
          )}
        </div>
        {remove.error && <small className="nw-missing">{remove.error.message}</small>}
      </td>
    </tr>
  )
}

function Since({ timestamp, never }: { timestamp: string | null; never: string }): ReactElement {
  const day = dayOf(timestamp)
  if (day === null) return <>{never}</>

  return (
    <>
      {describeAge(daysBetween(day, today()))}
      <small>{day}</small>
    </>
  )
}

function AddPasskey(): ReactElement {
  const [name, setName] = useState('')
  const add = useAddCredential()
  const authenticator = hasAuthenticator()

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    add.mutate({ name: name.trim() === '' ? undefined : name.trim() })
    setName('')
  }

  return (
    <>
      <form className="nw-add" onSubmit={submit}>
        <label htmlFor="nw-add-passkey">
          <span>Add a passkey</span>
          <input
            id="nw-add-passkey"
            type="text"
            placeholder="Phone, hardware key, laptop…"
            value={name}
            disabled={!authenticator || add.isPending}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <Button type="submit" kind="primary" disabled={!authenticator || add.isPending}>
          {add.isPending ? 'Waiting for your device…' : 'Register'}
        </Button>
      </form>

      {!authenticator && (
        <p className="nw-note">
          This browser cannot register a passkey. Open the dashboard on the device you want to
          enrol.
        </p>
      )}

      {add.error && (
        <p className="nw-flag nw-flag--missing" style={{ marginTop: 12 }}>
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>
            {add.error.message === 'credential_in_use'
              ? 'That device already holds a passkey for this dashboard. Enrolling it twice would look like two ways in when it is one.'
              : `Could not register that passkey: ${add.error.message}`}
          </span>
        </p>
      )}
    </>
  )
}

function Households({
  session,
  here,
}: {
  session: Session
  here: Membership | undefined
}): ReactElement {
  return (
    <Card>
      <CardHead
        id="nw-households-heading"
        title="Households"
        lede="One sign-in, several sets of books. Switching households in the bar above changes every figure on every page; nothing is ever added together across them."
      />

      <p className="nw-here">
        {here ? (
          <>
            Every page is currently about <b>{here.name}</b>, where you are{' '}
            {here.role === 'owner' ? 'an owner' : 'a member'}.
          </>
        ) : (
          <>
            This sign-in is not in a household, so there are no figures to show. Somebody who owns
            one has to invite you, or you can start one of your own below.
          </>
        )}
      </p>

      <DataTable columns={HOUSEHOLD_COLUMNS} label="Every household this sign-in belongs to">
          {session.tenants.map((membership) => (
            <tr key={membership.tenantId}>
              <th scope="row">{membership.name}</th>
              <td>
                <span className="nw-role">{membership.role}</span>
                <small>
                  {membership.role === 'owner'
                    ? 'You can invite and remove people here.'
                    : 'You can see the money; an owner manages who else does.'}
                </small>
              </td>
              <td>{membership.tenantId === session.tenantId ? 'Open now' : '—'}</td>
            </tr>
          ))}
      </DataTable>
    </Card>
  )
}

const ROLE_DIFFERENCE =
  'A member reads every figure in this household. An owner reads them too and can invite further ' +
  'people — including further owners — so it hands over the guest list as well as the books.'

/**
 * An invitation joins somebody to the household the session has open, because
 * that is the only household the server will read off it. There is nothing here
 * to name another one, and copy that suggested otherwise would be describing a
 * request that cannot be made.
 */
function Invitations({ household }: { household: Membership }): ReactElement {
  return (
    <Card>
      <CardHead
        id="nw-invite-heading"
        title={`Invite somebody into ${household.name}`}
        lede="They join this household and read its figures — the same figures, not a copy and not a set of books of their own. Nothing is sent for you: an invitation produces a link, and getting it to them is yours to do."
      />

      <InviteForm household={household} />
      <Pending />
    </Card>
  )
}

function InviteForm({ household }: { household: Membership }): ReactElement {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<TenantRole>('member')
  const invite = useInvite()

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    invite.mutate({ email: email.trim(), role })
  }

  return (
    <>
      <form className="nw-invite" onSubmit={submit} aria-label="Invite somebody">
        <label htmlFor="nw-invite-email">
          <span>Their email</span>
          <input
            id="nw-invite-email"
            type="email"
            autoComplete="off"
            value={email}
            disabled={invite.isPending}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label htmlFor="nw-invite-role">
          <span>Role</span>
          <select
            id="nw-invite-role"
            aria-describedby="nw-invite-role-note"
            value={role}
            disabled={invite.isPending}
            onChange={(event) => setRole(event.target.value as TenantRole)}
          >
            <option value="member">Member</option>
            <option value="owner">Owner</option>
          </select>
        </label>
        <Button type="submit" kind="primary" disabled={invite.isPending || email.trim() === ''}>
          {invite.isPending ? 'Touch your key…' : 'Write an invitation'}
        </Button>
      </form>

      <p className="nw-role-note" id="nw-invite-role-note">
        {ROLE_DIFFERENCE}
      </p>

      {invite.error && (
        <p className="nw-flag nw-flag--missing" style={{ marginTop: 12 }}>
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>No invitation was written: {describeRefusal(invite.error)}</span>
        </p>
      )}

      {invite.isSuccess && invite.data !== undefined && (
        <Issued issued={invite.data} household={household} />
      )}
    </>
  )
}

/**
 * The only moment the token exists in readable form. It is stored hashed, so no
 * later screen and no read of the database can produce it again — and somebody
 * who closes this expecting to find it later writes a second invitation without
 * ever learning that the first is now dead weight.
 */
function Issued({
  issued,
  household,
}: {
  issued: IssuedInvitation
  household: Membership
}): ReactElement {
  const [copied, setCopied] = useState(false)
  const link = invitationLink(issued.token)

  const copy = (): void => {
    void navigator.clipboard.writeText(link).then(
      () => setCopied(true),
      () => setCopied(false),
    )
  }

  return (
    <div className="nw-issued">
      <p className="nw-flag nw-flag--stale">
        <span className="nw-flag-icon" aria-hidden="true">
          !
        </span>
        <span>
          <b>This link is shown once and cannot be shown again.</b> It is stored hashed, so nothing
          can read it back — not this page, not the list below, not the database. Send it before you
          leave this page. If it is lost the only remedy is to write another one, and neither you
          nor {issued.email} will be able to tell which of the two still works.
        </span>
      </p>

      <label htmlFor="nw-invite-link">
        <span>Invitation link for {issued.email}</span>
        <input
          id="nw-invite-link"
          type="text"
          readOnly
          value={link}
          onFocus={(event) => event.target.select()}
        />
      </label>

      {canCopy() && (
        <div className="nw-row-actions">
          <Button onClick={copy}>{copied ? 'Copied' : 'Copy link'}</Button>
        </div>
      )}

      <p className="nw-lede" style={{ margin: '12px 0 0' }}>
        Opening it registers {issued.email} as {issued.role === 'owner' ? 'an owner' : 'a member'} of{' '}
        {household.name}. They have to sign in as <b>{issued.email}</b> — acceptance compares the
        address on the invitation against the address that signed in, so a link passed to anyone
        else admits nobody, and an address that is nearly right is refused with no explanation they
        can act on.
      </p>
    </div>
  )
}

function Pending(): ReactElement {
  const invitations = useInvitations()
  const pending = invitations.data?.invitations ?? []

  return (
    <>
      <h3>Written and not yet accepted</h3>

      {invitations.error && (
        <p className="nw-missing">
          Could not read the pending invitations: {invitations.error.message}
        </p>
      )}

      {!invitations.error && pending.length === 0 && (
        <p className="nw-none">
          Nobody is holding an unaccepted invitation to this household. One that is accepted leaves
          this list and turns up as a membership instead.
        </p>
      )}

      {pending.length > 0 && (
        <DataTable columns={PENDING_COLUMNS} label="Invitations written and not yet accepted">
            {pending.map((invitation) => (
              <tr key={`${invitation.email}/${invitation.createdAt}`}>
                <th scope="row">{invitation.email}</th>
                <td>
                  <span className="nw-role">{invitation.role}</span>
                </td>
                <td>
                  <Expiry expiresAt={invitation.expiresAt} />
                </td>
              </tr>
            ))}
        </DataTable>
      )}
    </>
  )
}

/**
 * The listing does not drop an invitation once it is too old to redeem, so a row
 * that has run out has to say so — otherwise it reads as somebody who simply has
 * not got round to it.
 */
function Expiry({ expiresAt }: { expiresAt: string }): ReactElement {
  const day = dayOf(expiresAt)
  if (day === null) return <>unknown</>

  const remaining = daysBetween(today(), day)
  return (
    <>
      {remaining <= 0 ? 'Expired — write another' : `in ${describeDuration(remaining)}`}
      <small>{day}</small>
    </>
  )
}

/**
 * A second household is a second set of books, not a second person. Putting it
 * beside the invitation form would invite the reading that inviting somebody
 * gives them a household — which is the one thing an invitation never does.
 */
function NewHousehold({ first }: { first: boolean }): ReactElement {
  const [name, setName] = useState('')
  const create = useCreateTenant()

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    create.mutate({ name: name.trim() }, { onSuccess: () => setName('') })
  }

  return (
    <Card>
      {/* "Another" is the wrong word for somebody who has none, and this card is
          the only way such a reader gets any — the dashboard sends them here. */}
      <CardHead
        id="nw-new-household-heading"
        title={first ? 'Start a household' : 'Start another household'}
        lede={
          first
            ? 'A set of books with you as its owner — your own finances, a company, a parent’s, anything whose total is nobody else’s business. You do not need an invitation to have one: creating it opens it immediately, and every figure in the product becomes its own.'
            : "A separate set of books with you as its owner — a company, a parent's finances, anything whose total is nobody else's business. Nothing is ever added across households, and nothing moves between them. Creating one opens it immediately, so every figure on every page becomes its own; the one you are in now is a switch away in the bar above."
        }
      />

      <form className="nw-add" onSubmit={submit}>
        <label htmlFor="nw-new-household">
          <span>Name it</span>
          <input
            id="nw-new-household"
            type="text"
            placeholder="The boat fund"
            value={name}
            disabled={create.isPending}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <Button type="submit" kind="primary" disabled={create.isPending || name.trim() === ''}>
          {create.isPending ? 'Creating…' : 'Create household'}
        </Button>
      </form>

      {create.error && (
        <p className="nw-flag nw-flag--missing" style={{ marginTop: 12 }}>
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>Could not create that household: {create.error.message}</span>
        </p>
      )}
    </Card>
  )
}

/**
 * Deleting the account, which the terms promise and nothing here could do.
 *
 * Last on the page and behind two presses, because it is the one act that
 * cannot be undone: it takes every household nobody else reads and every figure
 * in them, and there is no provider that will send the history again. So the
 * page asks the server what would go before offering to do it, prints that, and
 * then asks for the address to be typed back — and the route asks for the
 * passkey on top of all of it.
 */
function Retirement(): ReactElement {
  const [asked, setAsked] = useState(false)
  const [typed, setTyped] = useState('')
  const plan = useAccountDeletion(asked)
  const remove = useDeleteAccount()

  const blocked = plan.data?.blocked ?? []
  const matches =
    plan.data !== undefined && typed.trim().toLowerCase() === plan.data.confirmWith.toLowerCase()

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    remove.mutate({ confirm: typed.trim() })
  }

  return (
    <Card>
      <CardHead
        id="nw-retire-heading"
        title="Delete this account"
        lede="Your sign-in, your passkeys, and every household nobody else reads — with everything in them. Not hidden, not archived: removed. No bank, broker or chain will send this history again, and nothing here can put it back."
      />

      {!asked && (
        <Button kind="danger" onClick={() => setAsked(true)}>
          Delete this account
        </Button>
      )}

      {asked && plan.isPending && <p className="nw-none">Working out what would go…</p>}

      {asked && plan.error && (
        <p className="nw-missing">Could not work out what would go: {plan.error.message}</p>
      )}

      {asked && plan.data !== undefined && blocked.length > 0 && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>
            <b>Somebody else reads {blocked.map((one) => one.name).join(', ')}.</b> Deleting your
            account would either destroy their figures or leave them a household with no owner, so
            it is refused while they are in it. Remove{' '}
            {blocked.reduce((total, one) => total + one.others, 0) === 1
              ? 'the other member'
              : 'the other members'}{' '}
            first, or hand the household over by making one of them an owner and leaving it
            yourself.
          </span>
        </p>
      )}

      {asked && plan.data !== undefined && blocked.length === 0 && (
        <>
          {plan.data.households.length === 0 ? (
            <p className="nw-none">
              You hold no household of your own, so there are no figures to remove — only your
              sign-in and your passkeys.
            </p>
          ) : (
            <ul className="nw-going">
              {plan.data.households.map((household) => (
                <li key={household.tenantId}>
                  <b>{household.name}</b>, and everything it holds:{' '}
                  {holdingsOf(household.holds)
                    .map((holding) => `${holding.rows} ${holding.label}`)
                    .join(', ') || 'nothing yet'}
                  .
                </li>
              ))}
            </ul>
          )}

          {plan.data.leaving.length > 0 && (
            <p className="nw-role-note">
              {plan.data.leaving.map((one) => one.name).join(', ')} stays exactly as it is. You only
              belong to it, so what goes there is your way in and nothing else.
            </p>
          )}

          <form className="nw-add" onSubmit={submit}>
            <label htmlFor="nw-retire-confirm">
              <span>Type {plan.data.confirmWith} to confirm</span>
              <input
                id="nw-retire-confirm"
                type="text"
                autoComplete="off"
                value={typed}
                disabled={remove.isPending}
                onChange={(event) => setTyped(event.target.value)}
              />
            </label>
            <Button type="submit" kind="danger" disabled={!matches || remove.isPending}>
              {remove.isPending ? 'Touch your key…' : 'Delete it all for good'}
            </Button>
            <Button onClick={() => setAsked(false)} disabled={remove.isPending}>
              Keep my account
            </Button>
          </form>

          {remove.error && (
            <p className="nw-flag nw-flag--missing" style={{ marginTop: 12 }}>
              <span className="nw-flag-icon" aria-hidden="true">
                ✕
              </span>
              <span>Nothing was removed: {describeRefusal(remove.error)}</span>
            </p>
          )}
        </>
      )}
    </Card>
  )
}

/** The API answers in codes; nobody reading this page speaks them. */
function describeRefusal(error: Error): string {
  switch (error.message) {
    case 'forbidden':
      return 'only an owner of the household that is open can invite anyone into it.'
    case 'reassertion_required':
      return 'your passkey was not accepted.'
    case 'confirmation_mismatch':
      return 'what was typed did not match the address on this account.'
    case 'household_shared':
      return 'somebody else has joined one of your households since this page was drawn.'
    default:
      return error.message
  }
}

function canCopy(): boolean {
  return typeof globalThis.navigator?.clipboard?.writeText === 'function'
}
